import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid,
  Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel,
  Switch, Divider, Box, IconButton, CircularProgress, useTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { schoolService } from '../../services/schoolService';
import subscriptionService, { Plan } from '../../services/subscriptionService';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

// Standalone create flow — atomic on the backend, atomic in the UI. School +
// admin live in a single form so the user never sees a half-created school.
const validationSchema = yup.object({
  name: yup.string().trim().required('School name is required').min(3, 'At least 3 characters'),
  code: yup.string().trim().required('School code is required').matches(/^[A-Z0-9]{2,5}$/, 'Must be 2-5 uppercase letters / numbers'),
  email: yup.string().trim().email('Invalid email').required('School email is required'),
  phone: yup.string().trim(),
  address: yup.string().trim(),
  adminName: yup.string().trim().required('Admin name is required').min(3, 'At least 3 characters'),
  adminEmail: yup.string().trim().email('Invalid email').required('Admin email is required'),
  adminPassword: yup.string().required('Admin password is required').min(6, 'At least 6 characters'),
  planId: yup.string().required('Please select a subscription plan'),
  isTrial: yup.boolean(),
  trialDays: yup.number().min(1).max(365)
});

const generateSchoolCode = (name: string): string => {
  if (!name) return '';
  const common = new Set(['school', 'academy', 'institute', 'college', 'university', 'the', 'of', 'and']);
  const words = name.split(/\s+/).filter(w => w && !common.has(w.toLowerCase()));
  if (words.length === 0) return name.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
  if (words.length === 1) return words[0].replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
  const initials = words.slice(0, 5).map(w => w.charAt(0)).join('').toUpperCase();
  return initials.length < 2 ? words[0].replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() : initials;
};

const CreateSchoolDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [codeTouched, setCodeTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    subscriptionService.getAllPlans().then(setPlans).catch(() => { /* surfaced through formik */ });
  }, [open]);

  const formik = useFormik({
    initialValues: {
      name: '', code: '', email: '', phone: '', address: '',
      adminName: '', adminEmail: '', adminPassword: '',
      planId: '', isTrial: false, trialDays: 14
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await schoolService.createSchool({
          name: values.name.trim(),
          code: values.code.toUpperCase(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          address: values.address.trim(),
          adminName: values.adminName.trim(),
          adminEmail: values.adminEmail.trim(),
          adminPassword: values.adminPassword,
          planId: values.planId,
          isTrial: values.isTrial,
          trialDays: Number(values.trialDays)
        });
        enqueueSnackbar(`School "${values.name}" created`, { variant: 'success' });
        formik.resetForm();
        onCreated();
        onClose();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Failed to create school', { variant: 'error', autoHideDuration: 6000 });
      }
    }
  });

  // Auto-fill code from name until the user manually touches the code field.
  useEffect(() => {
    if (!codeTouched && formik.values.name) {
      const generated = generateSchoolCode(formik.values.name);
      if (generated !== formik.values.code) formik.setFieldValue('code', generated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name, codeTouched]);

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    setCodeTouched(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle component="div" sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Add New School</Typography>
          <Typography variant="caption" color="text.secondary">
            The school and its admin are created together. Either both succeed or neither does.
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={formik.isSubmitting}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 2.5 }}>
          <SectionLabel>School Details</SectionLabel>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" name="name" label="School Name" required
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" name="code" label="School Code" required
                value={formik.values.code}
                onChange={(e) => {
                  setCodeTouched(true);
                  const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
                  formik.setFieldValue('code', v);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={
                  formik.touched.code && formik.errors.code
                    ? formik.errors.code
                    : 'Auto-generated from name. Used for User IDs (e.g. ABC-000001).'
                }
                inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace', fontWeight: 600 }, maxLength: 5 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" name="email" type="email" label="School Email" required
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" name="phone" label="Phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" name="address" label="Address" multiline minRows={1}
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>
          </Grid>

          <SectionLabel sx={{ mt: 2 }}>School Admin</SectionLabel>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" name="adminName" label="Admin Name" required
                value={formik.values.adminName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.adminName && Boolean(formik.errors.adminName)}
                helperText={formik.touched.adminName && formik.errors.adminName}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" name="adminEmail" type="email" label="Admin Email" required
                value={formik.values.adminEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.adminEmail && Boolean(formik.errors.adminEmail)}
                helperText={formik.touched.adminEmail && formik.errors.adminEmail}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" name="adminPassword" type="password" label="Admin Password" required
                value={formik.values.adminPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.adminPassword && Boolean(formik.errors.adminPassword)}
                helperText={formik.touched.adminPassword && formik.errors.adminPassword ? formik.errors.adminPassword : 'At least 6 characters.'}
              />
            </Grid>
          </Grid>

          <SectionLabel sx={{ mt: 2 }}>Subscription</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl
                fullWidth size="small" required
                error={formik.touched.planId && Boolean(formik.errors.planId)}
              >
                <InputLabel id="plan-select-label">Subscription Plan</InputLabel>
                <Select
                  labelId="plan-select-label" name="planId" label="Subscription Plan"
                  value={formik.values.planId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {plans.length === 0 && <MenuItem value="" disabled><em>No plans available — create one in Plans first.</em></MenuItem>}
                  {plans.map(p => (
                    <MenuItem key={p._id} value={p._id}>
                      {p.name} · ${p.monthlyPrice}/mo · {p.features.length} features
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.planId && formik.errors.planId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formik.errors.planId}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: formik.values.isTrial ? 6 : 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.isTrial}
                    onChange={(e) => formik.setFieldValue('isTrial', e.target.checked)}
                    name="isTrial"
                  />
                }
                label="Enable free trial"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
                {formik.values.isTrial
                  ? 'School can use the platform immediately for the trial period.'
                  : 'Subscription starts as pending. Record payment in Manage Subscriptions to activate.'}
              </Typography>
            </Grid>
            {formik.values.isTrial && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth size="small" name="trialDays" type="number" label="Trial Duration (days)"
                  value={formik.values.trialDays}
                  onChange={formik.handleChange}
                  inputProps={{ min: 1, max: 365 }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleClose} disabled={formik.isSubmitting}>Cancel</Button>
          <Button
            type="submit" variant="contained"
            disabled={formik.isSubmitting}
            startIcon={formik.isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ borderRadius: 1.5, px: 3 }}
          >
            {formik.isSubmitting ? 'Creating…' : 'Create School'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1,
      color: 'text.secondary', fontSize: 11, mb: 1.5, ...sx
    }}
  >
    {children}
  </Typography>
);

export default CreateSchoolDialog;
